import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { getLocaleProfile } from '../../../shared/config/localeProfile';
import { FormField } from '../../../shared/ui/FormField';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CaptureFieldsEditor, CaptureField } from '../../../shared/ui/CaptureFieldsEditor';
import { SettingsIcon, BuildingIcon, PaletteIcon, CreditCardIcon, UsersIcon, ScrollTextIcon, BellIcon, ShieldIcon, FormInputIcon, PercentIcon, ShoppingBagIcon, PackageIcon, InfoIcon, TagIcon, DollarSignIcon, ClockIcon, UploadIcon, PlusIcon, TrashIcon, MailIcon, CheckCircleIcon, WalletIcon, LockIcon, AlertCircleIcon, FileTextIcon, MoreHorizontalIcon, ActivityIcon, SearchIcon, DownloadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon } from 'lucide-react';
type SettingsTab = 'company' | 'branding' | 'users' | 'payout' | 'general' | 'payment-providers' | 'receipt' | 'fields' | 'notifications' | 'security' | 'subscription-plan';
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: any;
  category: 'account' | 'payment' | 'ecommerce' | 'system';
}
interface ActivityLog {
  id: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  action: string;
  section: string;
  description: string;
  objectId: string;
  details: {
    amount?: string;
  };
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'warning' | 'error' | 'info';
}
export function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const currency = getTenant().currency.code;
  // ---- State ----
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: 'MBNK-MY-001234',
    businessName: 'Bila Bila Mart',
    registrationNumber: '202301234567',
    gstNumber: 'MY123456789',
    businessEmail: 'billing@bilabilamart.my',
    businessPhone: '+60 3-1234 5678',
    language: 'en-US'
  });
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: getTenant().brand.logo?.light || '',
    primaryColor: '#00539B',
    accentColor: '#2563eb'
  });
  const [generalSettings, setGeneralSettings] = useState({
    defaultExpiryDays: 7,
    redirectUrl: 'https://bilabilamart.my/payment-success',
    enableAutoReminders: true
  });
  const [receiptSettings, setReceiptSettings] = useState({
    showCompanyAddress: true,
    companyAddress: 'Lot 12, Jalan Sultan Ismail\nKuala Lumpur 50250\nMalaysia',
    ccEmail: 'receipts@bilabilamart.my',
    footerText: 'Thank you for your business!'
  });
  const [paymentMethods, setPaymentMethods] = useState([{
    id: 'visa',
    name: 'Visa',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'amex',
    name: 'American Express',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'fpx',
    name: 'FPX (Online Banking)',
    enabled: true,
    icon: BuildingIcon,
    category: 'banking'
  }, {
    id: 'tng',
    name: "Touch 'n Go eWallet",
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'grabpay',
    name: 'GrabPay',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'duitnow',
    name: 'DuitNow QR',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'boost',
    name: 'Boost',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'shopeepay',
    name: 'ShopeePay',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }]);
  const [users, setUsers] = useState([{
    id: '1',
    name: 'Ahmed Lee',
    email: 'ahmed@bilabilamart.my',
    role: 'Owner',
    status: 'Active'
  }, {
    id: '2',
    name: 'Aishath Tan',
    email: 'aishath@bilabilamart.my',
    role: 'Finance',
    status: 'Active'
  }, {
    id: '3',
    name: 'Ibrahim Rahman',
    email: 'ibrahim@bilabilamart.my',
    role: 'Operations',
    status: 'Active'
  }, {
    id: '4',
    name: 'Siti Nurhaliza',
    email: 'siti@bilabilamart.my',
    role: 'Cashier',
    status: 'Active'
  }, {
    id: '5',
    name: 'Wei Ling',
    email: 'weiling@bilabilamart.my',
    role: 'Cashier',
    status: 'Active'
  }, {
    id: '6',
    name: 'Muthu Kumar',
    email: 'muthu@bilabilamart.my',
    role: 'Cashier',
    status: 'Active'
  }, {
    id: '7',
    name: 'Nurul Amin',
    email: 'nurul@bilabilamart.my',
    role: 'Cashier',
    status: 'Active'
  }, {
    id: '8',
    name: 'Chong Wei',
    email: 'chongwei@bilabilamart.my',
    role: 'Cashier',
    status: 'Active'
  }]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAllInvitePerms, setShowAllInvitePerms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Operations');
  const [showRoleManagementModal, setShowRoleManagementModal] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: string;
  } | null>(null);
  // Role definitions with permissions
  const rolePermissions = {
    Owner: {
      description: 'Full access to all features and settings',
      permissions: {
        Dashboard: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Payments: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Users: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Reports: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        }
      }
    },
    'Merchant Management': {
      description: 'Create/edit/view merchant accounts, KYC, onboarding',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    'Account Management': {
      description: 'View and manage existing merchant accounts, support actions, no financial controls',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Sales: {
      description: 'View merchant pipeline, create leads, view commercial terms, no settlement/finance access',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Finance: {
      description: 'View settlements, payouts, MDR, invoices, refunds; no merchant onboarding rights',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: true
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: true,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Operations: {
      description: 'Transaction monitoring, dispute handling, reconciliation, basic merchant edits',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Cashier: {
      description: 'Process in-store payments and view customer info at the point of sale',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    }
  };
  const [termsSettings, setTermsSettings] = useState({
    termsText: 'By using our payment services, you agree to our terms and conditions.',
    privacyPolicy: 'We collect and process payment information in accordance with Malaysian data protection regulations.'
  });
  const [presetMetadataFields, setPresetMetadataFields] = useState<MetadataItem[]>([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number'
  }, {
    key: 'order_id',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Internal order ID'
  }]);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    paymentReceived: true,
    paymentFailed: true,
    refundProcessed: true,
    disputeOpened: true,
    settlementCompleted: true,
    lowBalance: true
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    apiKeyVisible: false
  });
  const [selectedShop, setSelectedShop] = useState('Beach Shop');
  const [shopSettings, setShopSettings] = useState({
    inventoryTracking: true,
    lowStockThreshold: 5,
    notifyOnLowStock: false,
    allowBackorders: false,
    hideOutOfStock: false,
    enableTaxCalculation: false,
    pricesIncludeTax: false,
    processingFee: 0,
    shippingFee: 0
  });
  const [taxRates, setTaxRates] = useState([{
    id: '1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: '2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: '3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: '1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: '3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: '4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [orderSettings, setOrderSettings] = useState({
    orderPrefix: 'ORD-',
    startingNumber: 1000
  });
  // Payout settings state
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [payoutDescriptor, setPayoutDescriptor] = useState('');
  const [shopFees, setShopFees] = useState([{
    id: '1',
    name: 'Processing Fee',
    type: 'Percentage',
    amount: 2.5,
    condition: 'Applied to all orders'
  }, {
    id: '2',
    name: 'Shipping Fee',
    type: 'Fixed',
    amount: 5.0,
    condition: 'Applied to orders over MYR 100'
  }]);
  const [shopPayoutSettings, setShopPayoutSettings] = useState({
    bankAccount: 'BML - 773000000001',
    frequency: 'Weekly',
    minimumAmount: 1000
  });
  const [shopNotifications, setShopNotifications] = useState({
    confirmationMessage: 'Thank you for your order! We will process it shortly.',
    notificationEmail: 'notifications@bilabilamart.my',
    notificationPhone: '+60 3-1234 5678',
    sendOrderNotifications: true,
    sendLowStockAlerts: false
  });
  // Activity Logs state
  const [activityLogs] = useState(() => {
    const logs = [];
    const now = new Date();
    const actions = [{
      type: 'created',
      section: 'Payments',
      desc: 'Payment created'
    }, {
      type: 'updated',
      section: 'Payments',
      desc: 'Payment updated'
    }, {
      type: 'deleted',
      section: 'Payments',
      desc: 'Payment deleted'
    }, {
      type: 'created',
      section: 'Customers',
      desc: 'Customer created'
    }, {
      type: 'updated',
      section: 'Customers',
      desc: 'Customer updated'
    }, {
      type: 'deleted',
      section: 'Customers',
      desc: 'Customer deleted'
    }, {
      type: 'created',
      section: 'Invoices',
      desc: 'Invoice created'
    }, {
      type: 'updated',
      section: 'Invoices',
      desc: 'Invoice updated'
    }, {
      type: 'approved',
      section: 'Bills',
      desc: 'Bill approved'
    }, {
      type: 'created',
      section: 'Refunds',
      desc: 'Refund created'
    }, {
      type: 'updated',
      section: 'Settings',
      desc: 'Settings updated'
    }, {
      type: 'created',
      section: 'Users',
      desc: 'User invited'
    }, {
      type: 'updated',
      section: 'Security',
      desc: 'Security settings updated'
    }];
    const realUsers = [{
      id: '1',
      name: 'Ahmed Lee',
      email: 'ahmed@bilabilamart.my',
      role: 'Owner'
    }, {
      id: '2',
      name: 'Aishath Tan',
      email: 'aishath@bilabilamart.my',
      role: 'Finance'
    }, {
      id: '3',
      name: 'Ibrahim Rahman',
      email: 'ibrahim@bilabilamart.my',
      role: 'Operations'
    }];
    for (let i = 0; i < 80; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const user = realUsers[Math.floor(Math.random() * realUsers.length)];
      const minutesAgo = Math.floor(Math.random() * 43200); // 30 days in minutes
      const timestamp = new Date(now.getTime() - minutesAgo * 60000);
      logs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        },
        action: action.type,
        section: action.section,
        description: action.desc,
        objectId: `${action.section.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`,
        details: {
          amount: Math.random() > 0.5 ? `MYR ${(Math.random() * 1000).toFixed(2)}` : undefined
        },
        ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3.'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });
  const [logFilters, setLogFilters] = useState({
    search: '',
    dateRange: '7days',
    user: 'all',
    actionType: 'all',
    section: 'all'
  });
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  // Merchant Requests state
  const [merchantRequests] = useState(() => {
    const now = new Date();
    return [{
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const tabs: TabConfig[] = [
  // ACCOUNT
  {
    id: 'company',
    label: 'Company',
    icon: BuildingIcon,
    category: 'account'
  }, {
    id: 'branding',
    label: 'Branding',
    icon: PaletteIcon,
    category: 'account'
  }, {
    id: 'users',
    label: 'Manage Users',
    icon: UsersIcon,
    category: 'account'
  }, {
    id: 'payout',
    label: 'Payout',
    icon: DollarSignIcon,
    category: 'account'
  },
  // PAYMENT SETTINGS
  {
    id: 'general',
    label: 'General',
    icon: SettingsIcon,
    category: 'payment'
  }, {
    id: 'payment-providers',
    label: 'Payment Providers',
    icon: CreditCardIcon,
    category: 'payment'
  }, {
    id: 'receipt',
    label: 'Receipt Information',
    icon: MailIcon,
    category: 'payment'
  }, {
    id: 'fields',
    label: 'Information Fields',
    icon: FormInputIcon,
    category: 'payment'
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    category: 'payment'
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldIcon,
    category: 'payment'
  },
  // E-COMMERCE
  {
    id: 'subscription-plan',
    label: 'Subscription Plan',
    icon: CreditCardIcon,
    category: 'account'
  }];
  const categories = [{
    id: 'account',
    label: 'ACCOUNT'
  }, {
    id: 'payment',
    label: 'PAYMENT SETTINGS'
  }];
  const SaveButton = () => <div className="pt-4" data-id="element-9693">
      <button className="px-4 py-2 bg-[#00539B] text-white font-medium rounded-md hover:bg-[#00457F] transition-colors" data-id="element-9694">
        Save Changes
      </button>
    </div>;
  // ---- Render functions ----
  const renderCompanySettings = () => <div className="space-y-6" data-id="element-9695">
      <div data-id="element-9696">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9697">
          Company Information
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9698">
          Manage your business details and merchant account information
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9699">
        <div className="flex items-start gap-3" data-id="element-9700">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9701" />
          <div className="text-sm text-gray-700" data-id="element-9702">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9703">
              Verified Merchant Account
            </p>
            <p className="text-xs" data-id="element-9704">
              Your merchant account has been verified and is active. Contact
              support if you need to update your business registration details.
            </p>
          </div>
        </div>
      </div>

      {/* Company Details Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9705">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9706">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9707">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-9708" />
            Business Details
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9709">
          <FormField label="Merchant ID" tooltip="Your unique merchant identifier" data-id="element-9710">
            <div className="flex items-center gap-2" data-id="element-9711">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" value={companyInfo.merchantId} readOnly data-id="element-9712" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap" data-id="element-9713">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-9714" />
                VERIFIED
              </span>
            </div>
          </FormField>

          <FormField label="Business Name" required data-id="element-9715">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessName} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessName: e.target.value
          })} data-id="element-9716" />
          </FormField>

          <FormField label="Registration Number" required data-id="element-9717">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.registrationNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            registrationNumber: e.target.value
          })} data-id="element-9718" />
          </FormField>

          <FormField label={getLocaleProfile().taxIdLabel} data-id="element-9719">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#00539B] focus:border-transparent" value={companyInfo.gstNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            gstNumber: e.target.value
          })} data-id="element-9720" />
          </FormField>

          <FormField label="Business Email" required data-id="element-9721">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessEmail} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessEmail: e.target.value
          })} data-id="element-9722" />
          </FormField>

          <FormField label="Business Phone" required data-id="element-9723">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessPhone} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessPhone: e.target.value
          })} data-id="element-9724" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9725" />
    </div>;
  const renderBrandingSettings = () => <div className="space-y-6" data-id="element-9726">
      <div data-id="element-9727">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9728">Branding</h2>
        <p className="text-sm text-gray-600" data-id="element-9729">
          Customize your brand appearance across payment pages and receipts
        </p>
      </div>

      {/* Logo Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9730">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9731">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9732">
            <PaletteIcon size={18} className="text-gray-600" data-id="element-9733" />
            Logo & Visual Identity
          </h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-9734">
          <FormField label="Company Logo" description="Upload your company logo (recommended: 200x60px, PNG or SVG)." data-id="element-9735">
            <div className="flex items-center gap-4" data-id="element-9736">
              {brandingSettings.logoUrl && <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-9737">
                  <img src={brandingSettings.logoUrl} alt="Company Logo" className="h-12 object-contain" data-id="element-9738" />
                </div>}
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center transition-colors" data-id="element-9739">
                <UploadIcon size={16} className="mr-2" data-id="element-9740" />
                Upload New Logo
              </button>
            </div>
          </FormField>

          <FormField label="Primary Brand Color" description="This color will be used for buttons and accents" data-id="element-9741">
            <div className="flex items-center gap-3" data-id="element-9742">
              <input type="color" className="h-10 w-20 border border-gray-300 rounded cursor-pointer" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-9743" />
              <input type="text" className="block w-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-9744" />
              <div className="flex-1 text-sm text-gray-600" data-id="element-9745">
                Current: BML Red
              </div>
            </div>
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9746" />
    </div>;
  const renderGeneralSettings = () => <div className="space-y-6" data-id="element-9747">
      <div data-id="element-9748">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9749">
          General Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9750">
          Configure default behaviors and preferences for your payment system
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9751">
        <div className="flex items-start gap-3" data-id="element-9752">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9753" />
          <div className="text-sm text-gray-700" data-id="element-9754">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9755">
              Payment Link Defaults
            </p>
            <p className="text-xs" data-id="element-9756">
              These settings apply to all new payment links and invoices. You
              can override them individually when creating each payment.
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9757">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9758">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9759">
            <SettingsIcon size={18} className="text-gray-600" data-id="element-9760" />
            Default Configuration
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9761">
          <FormField label="Default Expiry Days" description="Number of days before payment links expire" data-id="element-9762">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={generalSettings.defaultExpiryDays} onChange={e => setGeneralSettings({
            ...generalSettings,
            defaultExpiryDays: parseInt(e.target.value)
          })} data-id="element-9763" />
          </FormField>

          <FormField label="Redirect URL" description="Where customers are sent after successful payment" data-id="element-9764">
            <input type="url" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-mono text-sm" value={generalSettings.redirectUrl} onChange={e => setGeneralSettings({
            ...generalSettings,
            redirectUrl: e.target.value
          })} data-id="element-9765" />
          </FormField>

          <div className="pt-2" data-id="element-9766">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-9767">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={generalSettings.enableAutoReminders} onChange={e => setGeneralSettings({
              ...generalSettings,
              enableAutoReminders: e.target.checked
            })} data-id="element-9768" />
              <div data-id="element-9769">
                <p className="text-sm font-medium text-gray-900" data-id="element-9770">
                  Enable automatic payment reminders
                </p>
                <p className="text-xs text-gray-500" data-id="element-9771">
                  Send reminder emails for unpaid invoices
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-9772" />
    </div>;
  const renderReceiptSettings = () => <div className="space-y-6" data-id="element-9773">
      <div data-id="element-9774">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9775">
          Receipt Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9776">
          Customize the receipts sent to your customers after payment
        </p>
      </div>

      {/* Receipt Customization Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9777">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9778">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9779">
            <MailIcon size={18} className="text-gray-600" data-id="element-9780" />
            Receipt Customization
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9781">
          <div className="pb-2" data-id="element-9782">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-9783">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={receiptSettings.showCompanyAddress} onChange={e => setReceiptSettings({
              ...receiptSettings,
              showCompanyAddress: e.target.checked
            })} data-id="element-9784" />
              <div data-id="element-9785">
                <p className="text-sm font-medium text-gray-900" data-id="element-9786">
                  Show company address on receipts
                </p>
                <p className="text-xs text-gray-500" data-id="element-9787">
                  Display your business address on customer receipts
                </p>
              </div>
            </label>
          </div>

          <FormField label="Company Address" description="This address will appear on receipts if enabled above" data-id="element-9788">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={3} value={receiptSettings.companyAddress} onChange={e => setReceiptSettings({
            ...receiptSettings,
            companyAddress: e.target.value
          })} data-id="element-9789" />
          </FormField>

          <FormField label="CC Email" description="Automatically CC this email on all receipts" data-id="element-9790">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.ccEmail} onChange={e => setReceiptSettings({
            ...receiptSettings,
            ccEmail: e.target.value
          })} data-id="element-9791" />
          </FormField>

          <FormField label="Footer Text" description="Custom message at the bottom of receipts" data-id="element-9792">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.footerText} onChange={e => setReceiptSettings({
            ...receiptSettings,
            footerText: e.target.value
          })} data-id="element-9793" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9794" />
    </div>;
  const renderPaymentProviders = () => <div className="space-y-6" data-id="element-9795">
      <div data-id="element-9796">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9797">
          Payment Providers
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9798">
          Enable or disable payment methods available to your customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-9799">
        <div className="flex items-start gap-3" data-id="element-9800">
          <AlertCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9801" />
          <div className="text-sm text-gray-700" data-id="element-9802">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9803">
              Payment Method Availability
            </p>
            <p className="text-xs" data-id="element-9804">
              Disabling a payment method will hide it from all payment pages.
              Customers won't be able to use disabled methods for new payments.
            </p>
          </div>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9805">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9806">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9807">
            <CreditCardIcon size={18} className="text-gray-600" data-id="element-9808" />
            Card Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9809">
          {paymentMethods.filter(m => m.category === 'card').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9810">
                  <div className="flex items-center gap-3" data-id="element-9811">
                    <Icon size={20} className="text-gray-600" data-id="element-9812" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9813">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9814">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9815" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9816"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* E-Wallet Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9817">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9818">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9819">
            <WalletIcon size={18} className="text-gray-600" data-id="element-9820" />
            E-Wallets & QR Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9821">
          {paymentMethods.filter(m => m.category === 'ewallet').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9822">
                  <div className="flex items-center gap-3" data-id="element-9823">
                    <Icon size={20} className="text-gray-600" data-id="element-9824" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9825">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9826">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9827" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9828"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* Online Banking */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9829">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9830">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9831">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-9832" />
            Online Banking
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9833">
          {paymentMethods.filter(m => m.category === 'banking').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9834">
                  <div className="flex items-center gap-3" data-id="element-9835">
                    <Icon size={20} className="text-gray-600" data-id="element-9836" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9837">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9838">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9839" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9840"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="space-y-6" data-id="element-9841">
      <div className="flex items-center justify-between" data-id="element-9842">
        <div data-id="element-9843">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9844">
            Users & Permissions
          </h2>
          <p className="text-sm text-gray-600" data-id="element-9845">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-3" data-id="element-9846">
          <button onClick={() => setShowRoleManagementModal(true)} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md flex items-center hover:bg-gray-50 transition-colors" data-id="element-9847">
            <ShieldIcon size={16} className="mr-2" data-id="element-9848" />
            Permissions
          </button>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className="px-4 py-2 bg-[#00539B] text-white font-medium rounded-md flex items-center hover:bg-[#00457F] transition-colors" data-id="element-9849">
            <PlusIcon size={16} className="mr-2" data-id="element-9850" />
            Invite User
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9851">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9852">
            <h3 className="text-md font-semibold text-gray-900" data-id="element-9853">
              Invite New User
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-9854">
            <FormField label="Email Address" required data-id="element-9855">
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" data-id="element-9856" />
            </FormField>
            <FormField label="Role" required data-id="element-9857">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-9858">
                <option value="Owner" data-id="element-9859">Owner</option>
                <option value="Merchant Management" data-id="element-9860">Merchant Management</option>
                <option value="Account Management" data-id="element-9861">Account Management</option>
                <option value="Sales" data-id="element-9862">Sales</option>
                <option value="Finance" data-id="element-9863">Finance</option>
                <option value="Operations" data-id="element-9864">Operations</option>
                <option value="Cashier" data-id="element-9865">Cashier</option>
              </select>
              {inviteRole && rolePermissions[inviteRole as keyof typeof rolePermissions] && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-9866">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-9867">
                      {inviteRole}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-9868">
                      {rolePermissions[inviteRole as keyof typeof rolePermissions].description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-9869">
                      {Object.entries(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).slice(0, showAllInvitePerms ? Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length : 5).map(([section, perms]) => {
                const activePerms = Object.entries(perms).filter(([_, val]) => val).map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
                if (activePerms.length === 0) return null;
                return <span key={section} className="text-xs px-2 py-1 bg-white border border-blue-200 rounded text-gray-700" data-id="element-9870">
                              {section}: {activePerms.join(', ')}
                            </span>;
              })}
                      {Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length > 5 && <button type="button" onClick={() => setShowAllInvitePerms(!showAllInvitePerms)} className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" data-id="element-9871">
                          {showAllInvitePerms ? 'Show less' : `+${Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length - 5} more`}
                        </button>}
                    </div>
                  </div>}
            </FormField>
            <div className="flex gap-3 pt-2" data-id="element-9872">
              <button onClick={() => {
            if (inviteEmail && inviteRole) {
              const newUser = {
                id: `user-${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                role: inviteRole,
                status: 'Invited' as const
              };
              setUsers([...users, newUser]);
              setInviteEmail('');
              setInviteRole('Manager');
              setShowInviteForm(false);
            }
          }} className="px-4 py-2 bg-[#00539B] text-white font-medium rounded-md hover:bg-[#00457F] transition-colors" data-id="element-9873">
                Send Invite
              </button>
              <button onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
            setInviteRole('Operations');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-9874">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Users Table */}
      <div className="border border-gray-200 rounded-lg" data-id="element-9875">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg" data-id="element-9876">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9877">
            <UsersIcon size={18} className="text-gray-600" data-id="element-9878" />
            Team Members ({users.length})
          </h3>
        </div>
        <div data-id="element-9879">
          <table className="w-full" data-id="element-9880">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9881">
              <tr data-id="element-9882">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9883">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9884">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9885">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9886">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9887">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9888">
              {users.map(u => <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-id="element-9889">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-9890">
                    {u.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-9891">{u.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-9892">{u.role}</td>
                  <td className="px-6 py-4" data-id="element-9893">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.status === 'Active' ? 'bg-green-100 text-green-800' : u.status === 'Invited' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9894">
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-9895">
                    <div className="flex items-center justify-end gap-2" data-id="element-9896">
                      {u.role === 'Owner' ? <span className="text-gray-400 text-xs" data-id="element-9897">Owner</span> : <div className="relative inline-block" data-id="element-9898">
                          <button type="button" aria-label={`Actions for ${u.name}`} aria-haspopup="menu" aria-expanded={openUserMenuId === u.id} onClick={() => setOpenUserMenuId(openUserMenuId === u.id ? null : u.id)} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-9899">
                            <MoreHorizontalIcon size={18} data-id="element-9900" />
                          </button>
                          {openUserMenuId === u.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenUserMenuId(null)} data-id="element-9901" />
                              <div role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20" data-id="element-9902">
                                {u.status !== 'Invited' && <button role="menuitem" onClick={() => {
                          setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role
                          });
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9903">
                                    Edit
                                  </button>}
                                {u.status === 'Active' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Disabled' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-id="element-9904">
                                    Disable
                                  </button>}
                                {u.status === 'Disabled' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Active' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50" data-id="element-9905">
                                    Enable
                                  </button>}
                                <button role="menuitem" onClick={() => {
                          if (confirm(u.status === 'Invited' ? `Cancel invite for ${u.email}?` : `Are you sure you want to delete ${u.name}?`)) {
                            setUsers(users.filter(user => user.id !== u.id));
                          }
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-9906">
                                  {u.status === 'Invited' ? 'Cancel Invite' : 'Delete'}
                                </button>
                              </div>
                            </>}
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-9907">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-9908">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-9909">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9910">
                Edit Team Member
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-9911">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9912">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-9913" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-9914">
              <FormField label="Name" data-id="element-9915">
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-9916" />
              </FormField>
              <FormField label="Email" data-id="element-9917">
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-9918" />
              </FormField>
              <FormField label="Role" data-id="element-9919">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-9920">
                  <option value="Merchant Management" data-id="element-9921">
                    Merchant Management
                  </option>
                  <option value="Account Management" data-id="element-9922">Account Management</option>
                  <option value="Sales" data-id="element-9923">Sales</option>
                  <option value="Finance" data-id="element-9924">Finance</option>
                  <option value="Operations" data-id="element-9925">Operations</option>
                  <option value="Cashier" data-id="element-9926">Cashier</option>
                </select>
              </FormField>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50" data-id="element-9927">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-9928">
                Cancel
              </button>
              <button onClick={() => {
            setUsers(users.map(user => user.id === editingUser.id ? {
              ...user,
              name: editingUser.name,
              email: editingUser.email,
              role: editingUser.role
            } : user));
            setEditingUser(null);
          }} className="px-4 py-2 text-sm font-medium bg-[#00539B] text-white rounded-md hover:bg-[#00457F]" data-id="element-9929">
                Save Changes
              </button>
            </div>
          </div>
        </div>}

      {/* Role Management Modal */}
      {showRoleManagementModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-9930">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-9931">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-9932">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9933">
                Role Permissions Matrix
              </h3>
              <button onClick={() => setShowRoleManagementModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-9934">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9935">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-9936" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-9937">
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-9938">
                {Object.entries(rolePermissions).map(([role, config]) => <div key={role} className="border border-gray-200 rounded-lg p-4" data-id="element-9939">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-9940">{role}</h4>
                    <p className="text-xs text-gray-600" data-id="element-9941">
                      {config.description}
                    </p>
                  </div>)}
              </div>
              <div className="overflow-x-auto" data-id="element-9942">
                <table className="w-full border border-gray-200 rounded-lg" data-id="element-9943">
                  <thead className="bg-gray-50" data-id="element-9944">
                    <tr data-id="element-9945">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-9946">
                        Section
                      </th>
                      {Object.keys(rolePermissions).map(role => <th key={role} colSpan={5} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200" data-id="element-9947">
                          {role}
                        </th>)}
                    </tr>
                    <tr data-id="element-9948">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9949"></th>
                      {Object.keys(rolePermissions).map(role => <Fragment key={role} data-id="element-9950">
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-l border-gray-200" data-id="element-9951">
                            R
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9952">
                            W
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9953">
                            M
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9954">
                            I
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9955">
                            A
                          </th>
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-9956">
                    {Object.keys(rolePermissions.Owner.permissions).map(section => <tr key={section} className="hover:bg-gray-50" data-id="element-9957">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9958">
                            {section}
                          </td>
                          {Object.entries(rolePermissions).map(([role, config]) => {
                    const perms = config.permissions[section as keyof typeof config.permissions];
                    return <Fragment key={role} data-id="element-9959">
                                  <td className="px-2 py-3 text-center border-l border-gray-200" data-id="element-9960">
                                    {perms.read ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9961" /> : <span className="text-gray-300" data-id="element-9962">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9963">
                                    {perms.write ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9964" /> : <span className="text-gray-300" data-id="element-9965">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9966">
                                    {perms.modify ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9967" /> : <span className="text-gray-300" data-id="element-9968">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9969">
                                    {perms.insert ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9970" /> : <span className="text-gray-300" data-id="element-9971">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9972">
                                    {perms.approve ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9973" /> : <span className="text-gray-300" data-id="element-9974">—</span>}
                                  </td>
                                </Fragment>;
                  })}
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-gray-500" data-id="element-9975">
                <p className="font-medium mb-1" data-id="element-9976">Permission Legend:</p>
                <div className="flex flex-wrap gap-4" data-id="element-9977">
                  <span data-id="element-9978">
                    <strong data-id="element-9979">R</strong> = Read
                  </span>
                  <span data-id="element-9980">
                    <strong data-id="element-9981">W</strong> = Write
                  </span>
                  <span data-id="element-9982">
                    <strong data-id="element-9983">M</strong> = Modify
                  </span>
                  <span data-id="element-9984">
                    <strong data-id="element-9985">I</strong> = Insert
                  </span>
                  <span data-id="element-9986">
                    <strong data-id="element-9987">A</strong> = Approve
                  </span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-9988">
              <button onClick={() => setShowRoleManagementModal(false)} className="px-4 py-2 bg-[#00539B] text-white font-medium rounded-md hover:bg-[#00457F] transition-colors" data-id="element-9989">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
  const renderTerms = () => <div className="space-y-6" data-id="element-9990">
      <div data-id="element-9991">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9992">
          Terms & Privacy
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9993">
          Configure legal terms and privacy policy shown to customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9994">
        <div className="flex items-start gap-3" data-id="element-9995">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9996" />
          <div className="text-sm text-gray-700" data-id="element-9997">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9998">Legal Compliance</p>
            <p className="text-xs" data-id="element-9999">
              These terms are displayed to customers during checkout. Ensure
              they comply with Malaysian data protection and consumer laws.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10000">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10001">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10002">
            <FileTextIcon size={18} className="text-gray-600" data-id="element-10003" />
            Terms & Conditions
          </h3>
        </div>
        <div className="p-6" data-id="element-10004">
          <FormField label="Terms Text" description="Displayed on payment pages and checkout" data-id="element-10005">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.termsText} onChange={e => setTermsSettings({
            ...termsSettings,
            termsText: e.target.value
          })} data-id="element-10006" />
          </FormField>
        </div>
      </div>

      {/* Privacy Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10007">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10008">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10009">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10010" />
            Privacy Policy
          </h3>
        </div>
        <div className="p-6" data-id="element-10011">
          <FormField label="Privacy Policy Text" description="Information about how customer data is handled" data-id="element-10012">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.privacyPolicy} onChange={e => setTermsSettings({
            ...termsSettings,
            privacyPolicy: e.target.value
          })} data-id="element-10013" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10014" />
    </div>;
  const renderFields = () => <div className="space-y-6" data-id="element-10015">
      <div data-id="element-10016">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10017">
          Information Fields
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10018">
          Configure metadata fields collected at checkout
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10019">
        <div className="flex items-start gap-3" data-id="element-10020">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10021" />
          <div className="text-sm text-gray-700" data-id="element-10022">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10023">
              Custom Checkout Fields
            </p>
            <p className="text-xs" data-id="element-10024">
              Add custom fields to collect additional information from customers
              during checkout. This data will be attached to payment records and
              can be used for reporting or integration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Fields Editor Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10025">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10026">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10027">
            <FormInputIcon size={18} className="text-gray-600" data-id="element-10028" />
            Metadata Fields
          </h3>
        </div>
        <div className="p-6" data-id="element-10029">
          <MetadataEditor metadata={presetMetadataFields} onChange={setPresetMetadataFields} data-id="element-10030" />
        </div>
      </div>
    </div>;
  const renderNotifications = () => <div className="space-y-6" data-id="element-10031">
      <div data-id="element-10032">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10033">
          Notifications
        </h2>
        <p className="text-sm text-gray-600" data-id="element-10034">
          Manage how you receive alerts about payments and account activity
        </p>
      </div>

      {/* Notification Channels Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10035">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10036">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10037">
            <BellIcon size={18} className="text-gray-600" data-id="element-10038" />
            Notification Channels
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10039">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10040">
            <div data-id="element-10041">
              <p className="text-sm font-medium text-gray-900" data-id="element-10042">
                Email Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10043">
                Receive alerts via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10044">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.emailNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              emailNotifications: e.target.checked
            })} data-id="element-10045" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10046"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10047">
            <div data-id="element-10048">
              <p className="text-sm font-medium text-gray-900" data-id="element-10049">
                SMS Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10050">
                Receive alerts via text message
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10051">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.smsNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              smsNotifications: e.target.checked
            })} data-id="element-10052" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10053"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Event Notifications Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10054">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10055">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10056">
            <AlertCircleIcon size={18} className="text-gray-600" data-id="element-10057" />
            Event Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-10058">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10059">
            <div data-id="element-10060">
              <p className="text-sm font-medium text-gray-900" data-id="element-10061">
                Payment Received
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10062">
                When a customer completes payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10063">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentReceived} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentReceived: e.target.checked
            })} data-id="element-10064" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10065"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10066">
            <div data-id="element-10067">
              <p className="text-sm font-medium text-gray-900" data-id="element-10068">
                Payment Failed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10069">
                When a payment attempt fails
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10070">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentFailed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentFailed: e.target.checked
            })} data-id="element-10071" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10072"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10073">
            <div data-id="element-10074">
              <p className="text-sm font-medium text-gray-900" data-id="element-10075">
                Refund Processed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10076">
                When a refund is completed
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10077">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.refundProcessed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              refundProcessed: e.target.checked
            })} data-id="element-10078" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10079"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10080">
            <div data-id="element-10081">
              <p className="text-sm font-medium text-gray-900" data-id="element-10082">
                Dispute Opened
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10083">
                When a customer files a dispute
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10084">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.disputeOpened} onChange={e => setNotificationSettings({
              ...notificationSettings,
              disputeOpened: e.target.checked
            })} data-id="element-10085" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10086"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10087">
            <div data-id="element-10088">
              <p className="text-sm font-medium text-gray-900" data-id="element-10089">
                Settlement Completed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10090">
                When funds are transferred to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10091">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.settlementCompleted} onChange={e => setNotificationSettings({
              ...notificationSettings,
              settlementCompleted: e.target.checked
            })} data-id="element-10092" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10093"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-10094">
            <div data-id="element-10095">
              <p className="text-sm font-medium text-gray-900" data-id="element-10096">Low Balance</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-10097">
                When your account balance is low
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10098">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.lowBalance} onChange={e => setNotificationSettings({
              ...notificationSettings,
              lowBalance: e.target.checked
            })} data-id="element-10099" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10100"></div>
            </label>
          </div>
        </div>
      </div>
    </div>;
  const renderSecurity = () => <div className="space-y-6" data-id="element-10101">
      <div data-id="element-10102">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10103">Security</h2>
        <p className="text-sm text-gray-600" data-id="element-10104">
          Manage security settings and access controls for your account
        </p>
      </div>

      {/* Security Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-10105">
        <div className="flex items-start gap-3" data-id="element-10106">
          <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-10107" />
          <div className="text-sm text-gray-700" data-id="element-10108">
            <p className="font-medium text-gray-900 mb-1" data-id="element-10109">
              Security Status: Protected
            </p>
            <p className="text-xs" data-id="element-10110">
              Your account is secured with two-factor authentication and session
              management. Keep these settings enabled for maximum protection.
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10111">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10112">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10113">
            <LockIcon size={18} className="text-gray-600" data-id="element-10114" />
            Authentication & Access
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-10115">
          <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-4" data-id="element-10116">
            <div data-id="element-10117">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-10118">
                Two-Factor Authentication
                {securitySettings.twoFactorEnabled && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10119">
                    Enabled
                  </span>}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10120">
                Adds an extra layer of security to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-10121">
              <input type="checkbox" className="sr-only peer" checked={securitySettings.twoFactorEnabled} onChange={e => setSecuritySettings({
              ...securitySettings,
              twoFactorEnabled: e.target.checked
            })} data-id="element-10122" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10123"></div>
            </label>
          </div>

          <FormField label="Session Timeout (minutes)" description="Automatically log out after this period of inactivity" data-id="element-10124">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
            ...securitySettings,
            sessionTimeout: parseInt(e.target.value)
          })} data-id="element-10125" />
          </FormField>
        </div>
      </div>

      {/* IP Whitelist Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10126">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10127">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10128">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-10129" />
            IP Whitelist
          </h3>
        </div>
        <div className="p-6" data-id="element-10130">
          <FormField label="Allowed IP Addresses" description="Restrict API access to specific IP addresses (one per line). Leave empty to allow all IPs." data-id="element-10131">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={4} value={securitySettings.ipWhitelist} onChange={e => setSecuritySettings({
            ...securitySettings,
            ipWhitelist: e.target.value
          })} placeholder="192.168.1.1&#10;10.0.0.1" data-id="element-10132" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-10133" />
    </div>;
  const renderPayoutSettings = () => {
    const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
    let pendingPayoutUpdate: {
      bank?: string;
      maskedAccount?: string;
      submittedAt?: string;
    } | null = null;
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('pendingPayoutUpdate') : null;
      if (raw) pendingPayoutUpdate = JSON.parse(raw);
    } catch {
      pendingPayoutUpdate = null;
    }
    return <div className="space-y-6" data-id="element-10134">
        <div data-id="element-10135">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10136">
            Payout Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10137">
            Configure bank accounts, minimum balance, and payout preferences
          </p>
        </div>

        {/* Settlement Currencies Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10138">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10139">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10140">
              <BuildingIcon size={18} className="text-gray-600" data-id="element-10141" />
              Settlement Currencies & Bank Accounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10142">
              Configure bank accounts for receiving payouts in different
              currencies
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-10143">
            <table className="w-full text-left" data-id="element-10144">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10145">
                <tr data-id="element-10146">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10147">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10148">
                    Payout Bank Account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10149">
                    <div className="flex items-center gap-1.5" data-id="element-10150">
                      Payout Fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-10151" />
                    </div>
                  </th>
                  <th className="px-6 py-3" data-id="element-10152"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10153">
                <tr className="hover:bg-gray-50 transition-colors" data-id="element-10154">
                  <td className="px-6 py-4" data-id="element-10155">
                    <div className="flex items-center gap-2" data-id="element-10156">
                      <span className="text-sm font-medium text-gray-900" data-id="element-10157">
                        MYR
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10158">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10159">
                    <div className="flex flex-col gap-1.5" data-id="element-10160">
                      <div className="flex items-center gap-3" data-id="element-10161">
                        <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-10162">
                          <BuildingIcon size={16} className="text-gray-500" data-id="element-10163" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-10164">
                          <span className="text-sm text-gray-900" data-id="element-10165">
                            Bank Rakyat
                          </span>
                          <span className="text-sm text-gray-500 font-mono" data-id="element-10166">
                            ••••5678
                          </span>
                        </div>
                      </div>
                      {pendingPayoutUpdate && <div className="ml-11 flex items-center gap-2 flex-wrap" data-id="element-10167">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-id="element-10168">
                            <ClockIcon size={12} data-id="element-10169" />
                            Pending Bank Review
                          </span>
                          <span className="text-xs text-gray-600" data-id="element-10170">
                            New: {pendingPayoutUpdate.bank}{' '}
                            <span className="font-mono" data-id="element-10171">
                              {pendingPayoutUpdate.maskedAccount}
                            </span>
                          </span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-10172">
                    <span className="text-sm text-gray-600" data-id="element-10173">Free</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-10174">
                    <div className="flex items-center justify-end gap-2" data-id="element-10175">
                      <button onClick={() => navigate('/settings/payout-account/edit')} disabled={!!pendingPayoutUpdate} title={pendingPayoutUpdate ? 'An update is pending bank review' : 'Edit payout account'} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10176">
                        Edit
                      </button>
                      <button aria-label="More options" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-10177">
                        <MoreHorizontalIcon size={18} data-id="element-10178" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-10179">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-10180">
              + Add a settlement currency
            </button>
          </div>
        </div>

        {/* Minimum Balance Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10181">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10182">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10183">
              <DollarSignIcon size={18} className="text-gray-600" data-id="element-10184" />
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10185">
              Keep a minimum amount in your account to cover refunds and
              chargebacks
            </p>
          </div>

          <div className="p-6" data-id="element-10186">
            <div className="flex items-start gap-4" data-id="element-10187">
              <label className="relative inline-flex items-center cursor-pointer mt-0.5" data-id="element-10188">
                <input type="checkbox" className="sr-only peer" checked={minBalanceEnabled} onChange={e => setMinBalanceEnabled(e.target.checked)} data-id="element-10189" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-10190"></div>
              </label>

              <div className="flex-1" data-id="element-10191">
                <p className="text-sm font-medium text-gray-900" data-id="element-10192">
                  Keep a minimum amount in your account
                </p>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-10193">
                  We'll adjust your payouts so you always maintain this balance
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-10194">
                    <div className="relative max-w-xs" data-id="element-10195">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-10196">
                        <span className="text-gray-500 text-sm" data-id="element-10197">MYR</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-14 pr-3 py-2 text-sm rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent ${isMinBalanceInvalid ? 'border-red-300 text-red-900' : 'border-gray-300'}`} placeholder="0.00" data-id="element-10198" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-10199">
                        <InfoIcon size={14} data-id="element-10200" />
                        Amount must be greater than 0
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-10201">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10202">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isMinBalanceInvalid ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#00539B] hover:bg-[#00457F] text-white'}`} data-id="element-10203">
              Save
            </button>
          </div>
        </div>

        {/* Statement Descriptor Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10204">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10205">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10206">
              <FileTextIcon size={18} className="text-gray-600" data-id="element-10207" />
              Payout Statement Descriptor
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-10208">
              Customize how payouts appear on your bank statement
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-10209">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-10210">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10211" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-10212">
                Set a custom statement descriptor to help identify payouts in
                your bank account. This will appear on your bank statement for
                all payouts.
              </p>
            </div>

            <FormField label="Statement Descriptor" description="This text will appear on your bank statement" data-id="element-10213">
              <input type="text" value={payoutDescriptor} onChange={e => setPayoutDescriptor(e.target.value)} placeholder={`Example - ${companyInfo.businessName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" data-id="element-10214" />
            </FormField>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-10215">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10216">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium bg-[#00539B] hover:bg-[#00457F] text-white rounded-md transition-colors" data-id="element-10217">
              Save
            </button>
          </div>
        </div>
      </div>;
  };
  // MDR row component
  const MdrRow = ({
    type,
    tier,
    range,
    ic,
    sf,
    am,
    total,
    color
  }: any) => <tr className={color} data-id="element-10218">
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-10219">{type}</td>
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-10220">{tier}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10221">{range}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10222">{ic}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10223">{sf}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10224">{am}</td>
      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-10225">{total}</td>
    </tr>;
  const FixedFee = ({
    label,
    sublabel,
    amount
  }: {
    label: string;
    sublabel: string;
    amount: number | string;
  }) => <div className="flex items-center justify-between" data-id="element-10226">
      <div data-id="element-10227">
        <p className="text-sm text-gray-900 font-medium" data-id="element-10228">{label}</p>
        <p className="text-xs text-gray-500" data-id="element-10229">{sublabel}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900" data-id="element-10230">
        {typeof amount === 'number' ? formatCurrencyAmount(amount, {
        currency
      }) : amount}
      </span>
    </div>;
  const renderSubscriptionPlanSettings = () => <div data-id="element-10231">
      <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-10232">
        Subscription Plan
      </h2>
      <div className="space-y-6" data-id="element-10233">
        {/* Supported Payment Methods */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10234">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10235">
            <CreditCardIcon size={16} className="mr-2 text-blue-600" data-id="element-10236" />
            Supported Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10237">
            Payment methods enabled for your account with their merchant
            discount rates
          </p>

          {/* Card Payments */}
          <div className="mb-6" data-id="element-10238">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10239">
              Card Payments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10240">
              {/* Visa */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10241">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10242">
                  <div className="flex items-center justify-between" data-id="element-10243">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10244">
                      Visa
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10245">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10246">
                  <div className="flex items-center justify-between" data-id="element-10247">
                    <span className="text-xs text-gray-600" data-id="element-10248">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10249">
                      0.80% - 1.30%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10250">
                    <span className="text-xs text-gray-600" data-id="element-10251">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10252">
                      1.10% - 1.70%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10253">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10254">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10255">
                      <p className="text-xs text-gray-600" data-id="element-10256">
                        <span className="font-medium" data-id="element-10257">MYR 1-1,000:</span> 0.80%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10258">
                        <span className="font-medium" data-id="element-10259">MYR 1,000+:</span> 1.30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mastercard */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10260">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200" data-id="element-10261">
                  <div className="flex items-center justify-between" data-id="element-10262">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10263">
                      Mastercard
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10264">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10265">
                  <div className="flex items-center justify-between" data-id="element-10266">
                    <span className="text-xs text-gray-600" data-id="element-10267">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10268">
                      0.85% - 1.35%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10269">
                    <span className="text-xs text-gray-600" data-id="element-10270">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10271">
                      1.15% - 1.75%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10272">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10273">
                      + MYR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10274">
                      <p className="text-xs text-gray-600" data-id="element-10275">
                        <span className="font-medium" data-id="element-10276">MYR 1-500:</span> 0.85%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10277">
                        <span className="font-medium" data-id="element-10278">MYR 500+:</span> 1.35%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* American Express */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10279">
                <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200" data-id="element-10280">
                  <div className="flex items-center justify-between" data-id="element-10281">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10282">
                      American Express
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10283">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10284">
                  <div className="flex items-center justify-between" data-id="element-10285">
                    <span className="text-xs text-gray-600" data-id="element-10286">All Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10287">
                      2.40% - 2.60%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10288">
                    <p className="text-xs text-gray-500" data-id="element-10289">
                      + MYR 0.75 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* E-Wallets & Online Banking */}
          <div data-id="element-10290">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10291">
              E-Wallets & Online Banking
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10292">
              {/* Touch 'n Go */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10293">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10294">
                  <div className="flex items-center justify-between" data-id="element-10295">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10296">
                      Touch 'n Go
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10297">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10298">
                  <div className="flex items-center justify-between mb-2" data-id="element-10299">
                    <span className="text-xs text-gray-600" data-id="element-10300">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10301">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10302">
                    <p className="text-xs text-gray-500" data-id="element-10303">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* GrabPay */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10304">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200" data-id="element-10305">
                  <div className="flex items-center justify-between" data-id="element-10306">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10307">
                      GrabPay
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10308">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10309">
                  <div className="flex items-center justify-between mb-2" data-id="element-10310">
                    <span className="text-xs text-gray-600" data-id="element-10311">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10312">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10313">
                    <p className="text-xs text-gray-500" data-id="element-10314">
                      + MYR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* FPX */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10315">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200" data-id="element-10316">
                  <div className="flex items-center justify-between" data-id="element-10317">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10318">
                      FPX
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10319">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10320">
                  <div className="flex items-center justify-between mb-2" data-id="element-10321">
                    <span className="text-xs text-gray-600" data-id="element-10322">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10323">
                      1.20%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10324">
                    <p className="text-xs text-gray-500" data-id="element-10325">
                      + MYR 0.40 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* DuitNow QR */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10326">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200" data-id="element-10327">
                  <div className="flex items-center justify-between" data-id="element-10328">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10329">
                      DuitNow QR
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10330">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10331">
                  <div className="flex items-center justify-between mb-2" data-id="element-10332">
                    <span className="text-xs text-gray-600" data-id="element-10333">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10334">
                      0.80%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10335">
                    <p className="text-xs text-gray-500" data-id="element-10336">
                      + MYR 0.25 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10337">
            <div className="flex items-start gap-2" data-id="element-10338">
              <InfoIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10339" />
              <div className="text-sm text-gray-700" data-id="element-10340">
                <p className="font-medium text-gray-900 mb-1" data-id="element-10341">Pricing Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10342">
                  <li data-id="element-10343">
                    Card rates vary based on card type (debit/credit) and
                    issuing bank
                  </li>
                  <li data-id="element-10344">
                    Lower rates apply to Bank Rakyat-issued cards (on-us
                    transactions)
                  </li>
                  <li data-id="element-10345">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Fees & Additional Charges */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10346">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10347">
            <TagIcon size={16} className="mr-2 text-blue-600" data-id="element-10348" />
            Transaction Fees & Additional Charges
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10349">
            Fixed fees and additional charges per transaction or event
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-10350">
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10351">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10352">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-10353">
                  Card Transaction Fees
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-10354">
                <FixedFee label="Visa/Mastercard" sublabel="Per successful transaction" amount={0.5} data-id="element-10355" />
                <FixedFee label="American Express" sublabel="Per successful transaction" amount={0.75} data-id="element-10356" />
                <FixedFee label="Authorization Fee" sublabel="Per authorization attempt" amount={0.2} data-id="element-10357" />
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10358">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10359">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-10360">
                  E-Wallet & Online Banking
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-10361">
                <FixedFee label="Touch 'n Go / GrabPay" sublabel="Per successful transaction" amount={0.3} data-id="element-10362" />
                <FixedFee label="FPX (Online Banking)" sublabel="Per successful transaction" amount={0.4} data-id="element-10363" />
                <FixedFee label="DuitNow QR" sublabel="Per successful transaction" amount={0.25} data-id="element-10364" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10365">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10366">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-10367">
                Additional Service Fees
              </h4>
            </div>
            <table className="w-full text-left" data-id="element-10368">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10369">
                <tr data-id="element-10370">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-10371">
                    Service
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-10372">
                    Description
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-10373">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10374">
                <tr data-id="element-10375">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10376">
                    Refund Processing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10377">
                    Per refund transaction
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-10378">
                    {formatCurrencyAmount(1.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-10379">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10380">
                    Chargeback Fee
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10381">
                    Per chargeback filed
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-10382">
                    {formatCurrencyAmount(50.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-10383">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10384">
                    Chargeback Reversal
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10385">
                    When chargeback is won
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right" data-id="element-10386">{`-${formatCurrencyAmount(50.0, {
                    currency
                  })}`}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-10387">
            <div className="flex items-start gap-2" data-id="element-10388">
              <InfoIcon size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10389" />
              <div className="text-sm text-gray-700" data-id="element-10390">
                <p className="font-medium text-gray-900 mb-1" data-id="element-10391">Fee Structure:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10392">
                  <li data-id="element-10393">
                    <span className="font-medium" data-id="element-10394">Total cost</span> =
                    (Transaction Amount × MDR%) + Fixed Fee
                  </li>
                  <li data-id="element-10395">
                    Example: MYR 100 Visa on-us = (MYR 100 × 1.10%) + MYR 0.50 =
                    MYR 1.60
                  </li>
                  <li data-id="element-10396">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10397">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10398">
            <DollarSignIcon size={16} className="mr-2 text-blue-600" data-id="element-10399" />
            Subscription Fees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10400">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-10401">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10402">
                Annual Rental
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-10403">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10404">Billed annually</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-10405">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10406">
                Monthly Equivalent
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-10407">
                {formatCurrencyAmount(200, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10408">
                MYR 2,400 / 12 months
              </p>
            </div>
          </div>
        </div>

        {/* Last Payment */}
        <div data-id="element-10409">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-10410">
            <ClockIcon size={16} className="mr-2 text-blue-600" data-id="element-10411" />
            Last Subscription Payment
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-10412">
            <div data-id="element-10413">
              <p className="text-sm font-medium text-gray-900" data-id="element-10414">
                Annual Subscription Renewal
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10415">
                Payment Date: January 15, 2026
              </p>
            </div>
            <div className="text-right" data-id="element-10416">
              <p className="text-lg font-bold text-gray-900" data-id="element-10417">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-10418">
                Paid
              </span>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-10419">
            <p className="text-xs text-gray-600" data-id="element-10420">
              <span className="font-medium" data-id="element-10421">Next renewal:</span> January 15,
              2027
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderActivityLogs = () => {
    const filteredLogs = activityLogs.filter(log => {
      if (logFilters.search && !log.description.toLowerCase().includes(logFilters.search.toLowerCase()) && !log.user.name.toLowerCase().includes(logFilters.search.toLowerCase())) return false;
      if (logFilters.user !== 'all' && log.user.id !== logFilters.user) return false;
      if (logFilters.section !== 'all' && log.section !== logFilters.section) return false;
      if (logFilters.dateRange !== 'all') {
        const logDate = new Date(log.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
        if (logFilters.dateRange === '24h' && daysDiff > 1) return false;
        if (logFilters.dateRange === '7days' && daysDiff > 7) return false;
        if (logFilters.dateRange === '30days' && daysDiff > 30) return false;
      }
      return true;
    });
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const paginatedLogs = filteredLogs.slice((currentLogPage - 1) * logsPerPage, currentLogPage * logsPerPage);
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10422">
        <div data-id="element-10423">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10424">
            Activity Logs
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10425">
            View and track all user activities and system events
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10426">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10427">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10428">
              <SearchIcon size={18} className="text-gray-600" data-id="element-10429" />
              Filters
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10430">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10431">
              <div data-id="element-10432">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10433">
                  Search
                </label>
                <input type="text" placeholder="Search logs..." className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.search} onChange={e => setLogFilters({
                ...logFilters,
                search: e.target.value
              })} data-id="element-10434" />
              </div>
              <div data-id="element-10435">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10436">
                  Date Range
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.dateRange} onChange={e => setLogFilters({
                ...logFilters,
                dateRange: e.target.value
              })} data-id="element-10437">
                  <option value="all" data-id="element-10438">All Time</option>
                  <option value="24h" data-id="element-10439">Last 24 Hours</option>
                  <option value="7days" data-id="element-10440">Last 7 Days</option>
                  <option value="30days" data-id="element-10441">Last 30 Days</option>
                </select>
              </div>
              <div data-id="element-10442">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10443">
                  User
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.user} onChange={e => setLogFilters({
                ...logFilters,
                user: e.target.value
              })} data-id="element-10444">
                  <option value="all" data-id="element-10445">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-10446">
                      {u.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-10447">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10448">
                  Section
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.section} onChange={e => setLogFilters({
                ...logFilters,
                section: e.target.value
              })} data-id="element-10449">
                  <option value="all" data-id="element-10450">All Sections</option>
                  <option value="Payments" data-id="element-10451">Payments</option>
                  <option value="Customers" data-id="element-10452">Customers</option>
                  <option value="Invoices" data-id="element-10453">Invoices</option>
                  <option value="Bills" data-id="element-10454">Bills</option>
                  <option value="Refunds" data-id="element-10455">Refunds</option>
                  <option value="Users" data-id="element-10456">Users</option>
                  <option value="Settings" data-id="element-10457">Settings</option>
                  <option value="Security" data-id="element-10458">Security</option>
                </select>
              </div>
              <div data-id="element-10459">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10460">
                  Per Page
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logsPerPage} onChange={e => {
                setLogsPerPage(Number(e.target.value));
                setCurrentLogPage(1);
              }} data-id="element-10461">
                  <option value="25" data-id="element-10462">25</option>
                  <option value="50" data-id="element-10463">50</option>
                  <option value="100" data-id="element-10464">100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-10465">
              <p className="text-sm text-gray-600" data-id="element-10466">
                Showing {paginatedLogs.length} of {filteredLogs.length} logs
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10467">
          <div className="overflow-x-auto" data-id="element-10468">
            <table className="w-full" data-id="element-10469">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10470">
                <tr data-id="element-10471">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10472">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10473">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10474">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10475">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10476">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10477">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10478">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-10479">
                    <td className="px-6 py-4 text-sm text-gray-900" title={new Date(log.timestamp).toLocaleString()} data-id="element-10480">
                      {getRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10481">
                      <div className="flex items-center gap-2" data-id="element-10482">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold" data-id="element-10483">
                          {log.user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div data-id="element-10484">
                          <p className="text-sm font-medium text-gray-900" data-id="element-10485">
                            {log.user.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-10486">
                            {log.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-10487">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10488">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10489">
                      {log.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10490">
                      {log.objectId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10491">
                      {log.ipAddress}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-10492">
              <button onClick={() => setCurrentLogPage(Math.max(1, currentLogPage - 1))} disabled={currentLogPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10493">
                Previous
              </button>
              <span className="text-sm text-gray-600" data-id="element-10494">
                Page {currentLogPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentLogPage(Math.min(totalPages, currentLogPage + 1))} disabled={currentLogPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10495">
                Next
              </button>
            </div>}
        </div>
      </div>;
  };
  const renderRequests = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Closed':
          return 'bg-gray-100 text-gray-800';
        case 'Pending':
          return 'bg-amber-100 text-amber-800';
        case 'In Progress':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10496">
        <div data-id="element-10497">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10498">
            Merchant Requests
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10499">
            View and track requests for payment method activations and custom
            user roles
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10500">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10501">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10502">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-10503" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-10504">
            <table className="w-full" data-id="element-10505">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10506">
                <tr data-id="element-10507">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10508">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10509">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10510">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10511">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10512">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10513">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10514">
                {merchantRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 transition-colors" data-id="element-10515">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-10516">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10517">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-10518">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10519">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-10520">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10521">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-10522">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10523">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10524">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10525">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-10526">
              {merchantRequests.length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10527">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10528">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-10529">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10530">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10531">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-10532">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>
      </div>;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings();
      case 'branding':
        return renderBrandingSettings();
      case 'general':
        return renderGeneralSettings();
      case 'receipt':
        return renderReceiptSettings();
      case 'payment-providers':
        return renderPaymentProviders();
      case 'users':
        return renderUsers();
      case 'payout':
        return renderPayoutSettings();
      case 'fields':
        return renderFields();
      case 'notifications':
        return renderNotifications();
      case 'security':
        return renderSecurity();
      case 'subscription-plan':
        return renderSubscriptionPlanSettings();
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4" data-id="element-10533">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-10534">
        Settings
      </h1>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-10535">
        <nav className="lg:w-64 flex-shrink-0" data-id="element-10536">
          <div className="lg:hidden mb-4" data-id="element-10537">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-10538">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'bg-[#00539B] text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} data-id="element-10539">
                    <Icon size={16} data-id="element-10540" />
                    <span data-id="element-10541">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
          <div className="hidden lg:block" data-id="element-10542">
            <div className="space-y-6" data-id="element-10543">
              {categories.map(category => <div key={category.id} data-id="element-10544">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-10545">
                    {category.label}
                  </h3>
                  <ul className="space-y-1" data-id="element-10546">
                    {tabs.filter(tab => tab.category === category.id).map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <li key={tab.id} data-id="element-10547">
                            <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-[#00539B] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-10548">
                              <Icon size={16} data-id="element-10549" />
                              <span data-id="element-10550">{tab.label}</span>
                            </button>
                          </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-x-auto" data-id="element-10551">
          {renderActiveTab()}
        </div>
      </div>
    </div>;
}